import {
  useEffect, useRef, useState,
} from 'react';

import { useConfigStore, useMapStateStore, useMapInteractionStore } from '../../../stores';
import { animateMapNavigation, getCenter, shouldShowCommPin } from './GoogleMapHelpers';

export const addGoogleMapCommPin = (commPinLatLng, setCommPinLatLng) => {
  const commPinOverlayRef = useRef(null);
  // State flag to trigger the visibility effect after the overlay container is ready.
  const [overlayReady, setOverlayReady] = useState(false);

  // Map interaction store
  const map = useMapInteractionStore((state) => state.map);
  const isExternalFocus = useMapInteractionStore((state) => state.isExternalFocus);

  // Zustand stores
  const mapConfig = useConfigStore((state) => state.mapConfig);
  const config = useConfigStore((state) => state.config);
  const activeZoom = useMapStateStore((state) => state.activeZoom);

  const { googleMapsConfig } = mapConfig || {};
  const { communityLogo } = config || {};

  const {
    campusCenter,
    address,
  } = googleMapsConfig || {};
  const mapZoomToEnableZoomZones = googleMapsConfig?.zooms?.enableZoomZones;

  // Get community pin latlng.
  useEffect(() => {
    if (campusCenter || address) {
      const initializeCenter = async () => {
        const center = await getCenter(campusCenter, address);
        setCommPinLatLng(center);
      };

      initializeCenter().catch(() => {
        // Error handled silently
      });
    }
  }, [campusCenter, address]);

  // ── Zoom-zone-style HTML overlay ────────────────────────────────────
  // Renders the community pin as a circle icon with community logo,
  // positioned above the SVG overlay in floatPane.
  useEffect(() => {
    if (!map || !commPinLatLng || !communityLogo) return undefined;

    // Clean up previous overlay.
    if (commPinOverlayRef.current) {
      commPinOverlayRef.current.setMap(null);
      commPinOverlayRef.current = null;
      setOverlayReady(false);
    }

    const overlay = new window.google.maps.OverlayView();

    overlay.onAdd = function onAdd() {
      const container = document.createElement('div');
      container.className = 'communityLocationLabel';
      // Start hidden; the visibility effect will show it at the correct zoom.
      container.style.display = 'none';

      const icon = document.createElement('div');
      icon.className = 'communityLocationLabel__icon';
      const bg = document.createElement('div');
      bg.className = 'communityLocationLabel__icon__bg';
      const doughnut = document.createElement('div');
      doughnut.className = 'communityLocationLabel__icon__doughnut';
      const plus = document.createElement('div');
      plus.className = 'communityLocationLabel__icon__plus';
      icon.appendChild(bg);
      icon.appendChild(doughnut);
      icon.appendChild(plus);
      container.appendChild(icon);

      const label = document.createElement('div');
      label.className = 'communityLocationLabel__label';

      const img = document.createElement('img');
      img.className = 'communityLocationLabel__logo';
      img.src = communityLogo;
      img.alt = config.communityName || 'Community';
      label.appendChild(img);

      container.appendChild(label);

      // Click to zoom into the community at the zoom-zone activation level.
      container.style.cursor = 'pointer';
      container.title = 'Zoom to community';
      container.addEventListener('click', () => {
        const mapInstance = useMapInteractionStore.getState().map;
        const { googleMapsConfig: gc } = useConfigStore.getState().mapConfig || {};
        const targetZoom = gc?.zooms?.enableZoomZones;
        if (!mapInstance || !commPinLatLng || !targetZoom) return;
        const latLng = new window.google.maps.LatLng(commPinLatLng.lat, commPinLatLng.lng);
        animateMapNavigation(mapInstance, latLng, targetZoom, {
          onComplete: (finalZoom) => {
            if (finalZoom) useMapStateStore.getState().setActiveZoom(finalZoom);
          },
        });
      });

      this.container = container;
      // Append to floatPane (same pane as SVG overlay) with z-index to sit above it.
      this.getPanes().floatPane.appendChild(container);
      // Signal that the container is ready so the visibility effect can run.
      setOverlayReady(true);
    };

    overlay.draw = function draw() {
      const projection = this.getProjection();
      const position = projection.fromLatLngToDivPixel(
        new window.google.maps.LatLng(commPinLatLng.lat, commPinLatLng.lng),
      );
      if (position && this.container) {
        this.container.style.left = `${position.x}px`;
        this.container.style.top = `${position.y}px`;
      }
    };

    overlay.onRemove = function onRemove() {
      if (this.container && this.container.parentNode) {
        this.container.parentNode.removeChild(this.container);
      }
    };

    overlay.setMap(map);
    commPinOverlayRef.current = overlay;

    return () => {
      overlay.setMap(null);
      setOverlayReady(false);
    };
  }, [map, commPinLatLng, communityLogo]);

  // Visibility: show at every zoom below the zoom-zone level, including below
  // enableOverlay and in satellite view, where the SVG overlay is hidden.
  useEffect(() => {
    const overlayInstance = commPinOverlayRef.current;
    if (!overlayReady || !overlayInstance?.container) return;

    const shouldShow = shouldShowCommPin({
      isExternalFocus,
      activeZoom,
      enableZoomZones: mapZoomToEnableZoomZones,
    });

    overlayInstance.container.style.display = shouldShow ? '' : 'none';
  }, [overlayReady, isExternalFocus, activeZoom, mapZoomToEnableZoomZones]);
};

export default addGoogleMapCommPin;
